import type { ReactNode } from "react";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

let bodyScrollLocks = 0;
let previousBodyOverflow = "";

function lockBodyScroll() {
  if (bodyScrollLocks === 0) {
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  bodyScrollLocks += 1;
}

function unlockBodyScroll() {
  bodyScrollLocks = Math.max(0, bodyScrollLocks - 1);
  if (bodyScrollLocks === 0) {
    document.body.style.overflow = previousBodyOverflow;
  }
}

export function Modal({
  open,
  title,
  onClose,
  children,
  wide,
  eyebrow,
  drawer,
  drawerWide,
  headerActions,
  className = "",
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
  eyebrow?: string;
  drawer?: boolean;
  /** 联动编辑等宽抽屉（约 1080px） */
  drawerWide?: boolean;
  /** 右上角操作（如保存/关闭）；有内容时隐藏默认 X */
  headerActions?: ReactNode;
  className?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    // 后打开的弹窗先处理 ESC
    window.addEventListener("keydown", onKey, true);
    lockBodyScroll();
    return () => {
      window.removeEventListener("keydown", onKey, true);
      unlockBodyScroll();
    };
  }, [open, onClose]);

  if (!open) return null;

  const isDrawer = Boolean(drawer || drawerWide);

  return createPortal(
    <div
      className={`modal-backdrop${isDrawer ? " modal-backdrop-drawer" : ""}${className ? ` ${className}` : ""}`}
      role="presentation"
      onClick={onClose}
    >
      <div
        className={`modal${wide ? " modal-wide" : ""}${drawer ? " modal-drawer" : ""}${drawerWide ? " modal-drawer modal-drawer-wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <div className="modal-head-text">
            {eyebrow ? <div className="modal-eyebrow">{eyebrow}</div> : null}
            <h2>{title}</h2>
          </div>
          {headerActions ? (
            <div className="modal-head-actions">{headerActions}</div>
          ) : (
            <button
              type="button"
              className="icon-btn"
              onClick={onClose}
              aria-label="关闭"
            >
              <X size={18} />
            </button>
          )}
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
